import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, DoorOpen } from "lucide-react";
import { api, errMsg } from "../../../lib/api";
import { Loading } from "../../../components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { PageHead, Forbidden, Sel, resetMeta } from "./common";
import { Pill, DataTable, Tabs } from "../ui";
import { RoomCalendar } from "./RoomCalendar";

export const ROOM_KIND = { derslik: "Derslik", atis_poligonu: "Atış poligonu", uygulama_alani: "Uygulama alanı", diger: "Diğer" };
const EMPTY = { name: "", kind: "derslik", capacity: 0, location: "", note: "", active: true };

function RoomForm({ r, done }) {
  const [f, setF] = useState({ ...EMPTY, ...r });
  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, k === "capacity" ? Number(f[k]) : f[k]]));
    try { r?.id ? await api.put(`/admin/rooms/${r.id}`, body) : await api.post("/admin/rooms", body); resetMeta(); toast.success("Derslik kaydedildi"); done(); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <form onSubmit={save} className="grid gap-3 text-sm" data-testid="room-form">
      <label>Ad *<input className="field" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Örn. Derslik 1" data-testid="room-name" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label>Tür<Sel value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={ROOM_KIND} testid="room-kind" /></label>
        <label>Kapasite (0 = belirtilmedi)<input type="number" min={0} className="field" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="room-capacity" /></label>
      </div>
      <label>Konum<input className="field" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Kat, bina..." data-testid="room-location" /></label>
      <label>Not<textarea rows={2} className="field" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="room-note" /></label>
      <label className="flex gap-2"><input type="checkbox" className="accent-[#dc2626]" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="room-active" />Kullanımda (pasif derslik yeni derslere atanamaz; eski kayıtlar korunur)</label>
      <button className="btn-red" data-testid="room-save">Kaydet</button>
    </form>
  );
}

export default function Rooms() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("aktif");
  const [edit, setEdit] = useState(null);
  const [mode, setMode] = useState("liste");
  const load = useCallback(() => api.get("/admin/rooms").then((r) => setRows(r.data)).catch((x) => setErr(errMsg(x))), []);
  useEffect(() => { load(); }, [load]);
  if (err) return <Forbidden text={err} />;
  if (!rows) return <Loading />;
  const view = rows.filter((r) => tab === "all" || (tab === "aktif" ? r.active : !r.active));
  return (
    <div data-testid="rooms-page">
      <PageHead title="Derslikler" sub="Derslik, atış poligonu ve uygulama alanları. Aynı saatte aynı dersliğe veya eğitmene iki ders planlanamaz.">
        <button className="btn-red !py-2.5" onClick={() => setEdit({})} data-testid="room-add-btn"><Plus size={16} />Yeni Derslik</button>
      </PageHead>
      <Tabs tabs={[["liste", "Derslik Listesi"], ["takvim", "Haftalık Takvim"]]} value={mode} onChange={setMode} testid="room-mode" />
      {mode === "takvim" ? <RoomCalendar /> : <>
      <Tabs tabs={[["aktif", "Kullanımda", rows.filter((r) => r.active).length], ["pasif", "Pasif", rows.filter((r) => !r.active).length], ["all", "Tümü", rows.length]]} value={tab} onChange={setTab} testid="room-tabs" />
      <DataTable testid="rooms-table" rows={view} empty="Henüz derslik eklenmedi." cols={[
        { key: "name", label: "Ad", sort: (r) => r.name, render: (r) => <span className="flex items-center gap-2 font-bold"><DoorOpen size={15} className="text-[#dc2626]" />{r.name}</span> },
        { key: "kind", label: "Tür", render: (r) => ROOM_KIND[r.kind] },
        { key: "cap", label: "Kapasite", sort: (r) => r.capacity, render: (r) => r.capacity || "-" },
        { key: "loc", label: "Konum", render: (r) => r.location || "-" },
        { key: "cls", label: "Varsayılan sınıf", sort: (r) => r.classes, render: (r) => r.classes },
        { key: "up", label: "Yaklaşan ders", sort: (r) => r.upcoming_sessions, render: (r) => r.upcoming_sessions },
        { key: "st", label: "Durum", render: (r) => <Pill s={r.active ? "aktif" : "iptal"}>{r.active ? "Kullanımda" : "Pasif"}</Pill> },
        { key: "act", label: "İşlemler", render: (r) => <button className="grid place-items-center h-8 w-8 rounded-md hover:bg-zinc-100" onClick={() => setEdit(r)} aria-label="Düzenle" data-testid={`room-edit-${r.id}`}><Pencil size={15} /></button> },
      ]} /></>}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}><DialogContent className="max-w-lg rounded-xl"><DialogHeader><DialogTitle>{edit?.id ? edit.name : "Yeni derslik"}</DialogTitle></DialogHeader>
        {edit && <RoomForm r={edit} done={() => { setEdit(null); load(); }} />}</DialogContent></Dialog>
    </div>
  );
}
